import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Hash, ListFilter, Upload, X } from "lucide-react";
import { type AudienceSource, useAudienceSources } from "@/lib/audience-sources";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface SourceSelection {
  channelIds: string[];
  importSources: string[];
}

/**
 * Where an audience comes from, as a list you can actually look at.
 *
 * Sources are of two kinds and both belong here: channels the scraper walked,
 * and lists the operator uploaded — a company that works from uploads has no
 * channels at all. Each row carries its head-count, so "what do I even have,
 * and how many people are in it" is answered before anything is selected.
 *
 * Used by the audience page (as a filter) and by the campaign form (as the
 * campaign's source), so both show the same list with the same numbers.
 */
export function SourcePicker({
  selection,
  onChange,
  triggerLabel,
  footer,
  align = "start",
}: {
  selection: SourceSelection;
  onChange: (next: SourceSelection) => void;
  /** Overrides the default "All sources / N selected" trigger text. */
  triggerLabel?: string;
  /** Rendered next to the trigger — e.g. the campaign's head-count. */
  footer?: ReactNode;
  align?: "start" | "center" | "end";
}) {
  const { t } = useTranslation();
  const sources = useAudienceSources();
  const [query, setQuery] = useState("");

  const all = useMemo(() => sources.data ?? [], [sources.data]);
  const { channelIds, importSources } = selection;
  const selectedCount = channelIds.length + importSources.length;

  const isSelected = (s: AudienceSource) =>
    s.kind === "channel" ? channelIds.includes(s.id) : importSources.includes(s.id);

  const toggle = (s: AudienceSource) => {
    if (s.kind === "channel") {
      onChange({
        channelIds: channelIds.includes(s.id)
          ? channelIds.filter((x) => x !== s.id)
          : [...channelIds, s.id],
        importSources,
      });
    } else {
      onChange({
        channelIds,
        importSources: importSources.includes(s.id)
          ? importSources.filter((x) => x !== s.id)
          : [...importSources, s.id],
      });
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? all.filter((s) => s.label.toLowerCase().includes(q)) : all;
  }, [all, query]);

  const selectedLabels = useMemo(
    () =>
      all.filter((s) =>
        s.kind === "channel" ? channelIds.includes(s.id) : importSources.includes(s.id),
      ),
    [all, channelIds, importSources],
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconLeft={<ListFilter size={15} strokeWidth={2} aria-hidden="true" />}
            >
              {triggerLabel ??
                (selectedCount === 0
                  ? t("audience.source.all_sources")
                  : t("audience.source.n_selected", { count: selectedCount }))}
            </Button>
          </PopoverTrigger>
          <PopoverContent align={align} className="w-96 p-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("audience.source.search")}
              className="mb-2 h-8"
            />
            <div className="max-h-72 space-y-0.5 overflow-y-auto">
              {sources.isPending ? (
                <p className="px-2 py-3 text-center text-xs text-fg-muted">
                  {t("audience.source.loading")}
                </p>
              ) : filtered.length === 0 ? (
                <p className="px-2 py-3 text-center text-xs text-fg-muted">
                  {all.length === 0
                    ? t("audience.source.none_yet")
                    : t("audience.source.nothing_found")}
                </p>
              ) : (
                filtered.map((s) => (
                  <label
                    key={`${s.kind}:${s.id}`}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-bg-secondary"
                  >
                    <Checkbox checked={isSelected(s)} onCheckedChange={() => toggle(s)} />
                    {s.kind === "channel" ? (
                      <Hash size={13} className="shrink-0 text-fg-muted" aria-hidden="true" />
                    ) : (
                      <Upload size={13} className="shrink-0 text-fg-muted" aria-hidden="true" />
                    )}
                    <span className="truncate text-sm">{s.label}</span>
                    <span
                      className="ml-auto shrink-0 font-mono text-xs tabular-nums text-fg-muted"
                      // Bots are excluded here because campaigns always exclude
                      // them; the audience table counts them unless you tick
                      // "hide bots", so the two numbers can differ slightly.
                      title={t("audience.source.people_hint")}
                    >
                      {s.people}
                    </span>
                  </label>
                ))
              )}
            </div>
            {filtered.length > 0 && (
              <div className="mt-2 flex gap-2 border-t border-border pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  onClick={() =>
                    onChange({
                      channelIds: Array.from(
                        new Set([
                          ...channelIds,
                          ...filtered.filter((s) => s.kind === "channel").map((s) => s.id),
                        ]),
                      ),
                      importSources: Array.from(
                        new Set([
                          ...importSources,
                          ...filtered.filter((s) => s.kind === "import").map((s) => s.id),
                        ]),
                      ),
                    })
                  }
                >
                  {t("audience.source.select_shown")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  disabled={selectedCount === 0}
                  onClick={() => onChange({ channelIds: [], importSources: [] })}
                >
                  {t("audience.source.clear")}
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {footer}
      </div>

      {selectedLabels.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedLabels.map((s) => (
            <Badge
              key={`${s.kind}:${s.id}`}
              tone={s.kind === "channel" ? "accent" : "neutral"}
              variant="subtle"
              className="max-w-[220px] truncate px-2 py-0.5"
            >
              <span className="truncate">{s.label}</span>
              <button
                type="button"
                aria-label={t("audience.source.remove_one")}
                onClick={() => toggle(s)}
                className="ml-1 opacity-70 hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
